/**
 * NPCs fighting for themselves (lane C0-NPC N2; data/npcEvents.ts): the small events of the plains, run inside the
 * life system (systems/npcLife.ts owns the people; this module only stages the trouble). An event picks its moment
 * and its person, gives its tell (a cry, an icon over the head, a ping the minimap draws from `active`), spawns the
 * foes and plays the fight — the foes' blows with a wind-up, the people's blows through the World's blow rules — and
 * ends in a win, a loss (hurt: home to rest a day, never dead) or thanks and a gift when the player helped.
 *
 * Deterministic: the life system's seeded stream for every choice; the blows that land on monsters go through
 * `SystemHost.hit` like any blow.
 */
import { LIFE_EVENT, LIFE_EVENT_IDS, LIFE_EVENTS, type EventWhere, type LifeEventDef, type LifeEventId } from '../data/npcEvents';
import { EMOJI_SECONDS } from '../data/emotes';
import { GOODS } from '../data/professions';
import type { LifeActId } from '../data/npcLife';
import { PLAINS_SIZE } from '../data/layouts/plains';
import type { GameMap, Point } from '../map';
import { freeAt, nearestFloor } from '../path';
import type { Rng } from '../rng';
import type { Entity, WorldEvent } from '../world';
import { AFFINITY } from '../data/npcAffinity';
import { addAffinity, addScore, memoryKey, memoryOf, setMemoryKey } from './npcMemory';
import type { SystemHost } from './types';

/** What the life system lends the events. */
export interface LifeEventHost {
  readonly h: SystemHost;
  readonly rnd: Rng;
  /** The town's people out of doors, by job and act. */
  people(): readonly { id: string; job: string; act: LifeActId | null; walking: boolean; e: Entity | null; held: boolean }[];
  /** Stops a person's own life for the event (they stand and fight, or lie hurt), or gives it back. */
  hold(id: string, on: boolean): void;
  /** Home to rest an in-game day; family and friends will call. */
  injure(id: string): void;
  /** Ties of a person (ids), for the memory of their family and friends. */
  tiesOf(id: string): readonly string[];
  memoryKeyOf(id: string): string | null;
  /** The teacher walks over to the hurt disciple. */
  sendTo(id: string, x: number, y: number): void;
  bark(e: Entity, text: string): void;
  rumour(who: string, x: number, y: number): void;
}

interface Fighter {
  e: Entity;
  /** A town person's id, or null for an actor made for the event. */
  id: string | null;
  hp: number;
  swingT: number;
  down: boolean;
}

interface Foe {
  e: Entity;
  swingT: number;
  /** A blow wound up: lands on this fighter when it runs out. */
  windT: number;
  at: Fighter | null;
}

/** An event under way (the minimap's ping reads `x`, `y`). */
export interface ActiveEvent {
  readonly id: LifeEventId;
  readonly def: LifeEventDef;
  readonly x: number;
  readonly y: number;
  t: number;
  phase: 'tell' | 'fight' | 'over';
  readonly fighters: Fighter[];
  readonly foes: Foe[];
  helped: boolean;
  /** The hurt disciple: seconds the player has stood beside them. */
  helpT: number;
  overT: number;
  outcome: 'won' | 'helped' | 'lost' | null;
  cryT: number;
}

export class LifeEvents {
  active: ActiveEvent | null = null;
  /** World time of the next event, and when each last happened. */
  /** World time of the next event (−1: not set yet). */
  nextAt = -1;
  private readonly last = new Map<LifeEventId, number>();
  /** Foes left behind, slinking off. */
  private readonly leftovers: { e: Entity; t: number }[] = [];

  constructor(private readonly l: LifeEventHost) {}

  private get w() {
    return this.l.h.world;
  }

  private range([a, b]: readonly [number, number]): number {
    return a + this.l.rnd() * (b - a);
  }

  step(dt: number): void {
    const w = this.w;
    if (this.nextAt < 0) this.nextAt = w.time + this.range(LIFE_EVENT.first);
    this.tidy(dt);
    const ev = this.active;
    if (ev) return this.run(ev, dt);
    if (w.time >= this.nextAt) {
      // Not while the player is busy with something of their own (a bout, the group dance, a fight, a cutscene).
      const p = w.player;
      if (p.dead || p.combatT > 0 || w.duel || w.dance.holds(p) || w.story.active) {
        this.nextAt = w.time + 30;
        return;
      }
      this.nextAt = w.time + this.range(LIFE_EVENT.gap);
      this.start();
    }
  }

  /** Starts an event now (tests, the GM): the one named, else one that fits. */
  start(only?: LifeEventId): boolean {
    if (this.active) return false;
    const w = this.w;
    const ids = only ? [only] : LIFE_EVENT_IDS.filter((id) => w.time - (this.last.get(id) ?? -1e9) >= LIFE_EVENT.again);
    // A fair order from the stream (the same each run).
    const order = ids.map((id) => [id, this.l.rnd()] as const).sort((a, b) => a[1] - b[1]);
    for (const [id] of order) if (this.begin(id, !only)) return true;
    return false;
  }

  private begin(id: LifeEventId, away = false): boolean {
    const def = LIFE_EVENTS[id];
    const w = this.w;
    const map = w.map;
    const fighters: Fighter[] = [];
    let at: Point | null = null;
    if (def.victim) {
      const v = def.victim;
      const can = this.l
        .people()
        .filter((p) => p.e && !p.walking && !p.held && v.jobs.includes(p.job as never) && p.act && v.acts.includes(p.act));
      const far = away ? can.filter((p) => !this.tooNear(p.e!)) : can;
      if (!far.length) return false;
      const p = far[Math.floor(this.l.rnd() * far.length)]!;
      const e = p.e!;
      this.l.hold(p.id, true);
      fighters.push({ e, id: p.id, hp: def.fighter.hp, swingT: 1, down: false });
      at = { x: e.x, y: e.y };
    } else if (def.actors && def.where) {
      for (const wh of def.where) {
        at = this.where(map, wh);
        if (at) break;
      }
      if (!at || (away && this.tooNear(at))) return false;
      def.actors.forEach((a, k) => {
        const spot = this.floor(map, at!.x + (k ? (k % 2 ? 1.2 : -1.2) : 0), at!.y + (k ? 0.8 : 0)) ?? at!;
        const e = this.l.h.add({ kind: 'ambient', x: spot.x, y: spot.y, name: a.name, title: a.title, sprite: a.sprite, speed: 1.8, radius: 0.28 });
        setMemoryKey(e, `${map.zone}/eventActor/${a.name}`);
        fighters.push({ e, id: null, hp: def.fighter.hp, swingT: 1 + k * 0.4, down: false });
      });
    } else return false;
    for (const f of fighters) {
      f.e.emote = null;
      f.e.atk = def.fighter.atk;
      f.e.hit = 10;
      f.e.maxHp = 100;
      f.e.hp = 100;
      f.e.crit = 0.05;
    }
    const foes: Foe[] = [];
    if (def.foe)
      for (let k = 0; k < def.foe.n; k++) {
        const a = (k / def.foe.n) * Math.PI * 2 + this.l.rnd();
        let p: Point | null = null;
        // Out among the trees or rocks: closer in until there is ground.
        for (const r of [LIFE_EVENT.foeAt, 4.5, 3, 2]) if (!p) p = this.floor(map, at.x + Math.cos(a) * r, at.y + Math.sin(a) * r);
        p ??= this.floor(map, at.x + Math.cos(a) * 2, at.y + Math.sin(a) * 2, 8);
        if (!p) continue;
        const e = this.l.h.spawnMonster(def.foe.monster, p.x, p.y);
        foes.push({ e, swingT: 1 + this.l.rnd(), windT: 0, at: null });
      }
    if (def.foe && !foes.length) {
      this.release(fighters);
      return false;
    }
    const ev: ActiveEvent = {
      id,
      def,
      x: at.x,
      y: at.y,
      t: 0,
      phase: 'tell',
      fighters,
      foes,
      helped: false,
      helpT: 0,
      overT: 0,
      outcome: null,
      cryT: 0,
    };
    this.active = ev;
    this.last.set(id, w.time);
    // The tell: a cry, an icon over the head, and word of it when the player is within earshot of the town.
    const v = fighters[0]!.e;
    this.cry(ev);
    v.emoji = 'exclaim';
    v.emojiT = EMOJI_SECONDS * 2;
    this.l.h.emit({ type: 'emoji', id: v.id, emoji: 'exclaim' });
    const p = w.player;
    if (Math.hypot(p.x - at.x, p.y - at.y) < LIFE_EVENT.heard) this.l.h.notice(`${def.name}: ${v.name}ร้องขอความช่วยเหลือ`);
    if (id === 'wanderer') this.l.h.log('sys', `${v.name}: ${def.cry[0]}`);
    if (!def.foe) this.down(ev, fighters[0]!);
    return true;
  }

  /** Trouble starts out of the player's way: they hear of it and come (or not). */
  private tooNear(at: Point): boolean {
    const p = this.w.player;
    return Math.hypot(p.x - at.x, p.y - at.y) < LIFE_EVENT.away;
  }

  private where(map: GameMap, wh: EventWhere): Point | null {
    const big = map.zone === 'plains' && map.w === PLAINS_SIZE;
    if ('big' in wh) return big ? this.floor(map, wh.big[0] + 0.5, wh.big[1] + 0.5) : null;
    if ('mark' in wh) {
      const b = wh.mark === 'plaza' ? map.plaza : map.landmarks?.[wh.mark];
      return b ? this.floor(map, b.x + (wh.dx ?? 0), b.y + (wh.dy ?? 0)) : null;
    }
    const r = map.regions?.[wh.region as keyof typeof map.regions];
    if (!r) return null;
    const [i0, i1, j0, j1] = r;
    let x = (i0 + i1) / 2;
    let y = (j0 + j1) / 2;
    if (wh.side === 'near') {
      // The region's rim nearest the plaza: on the road in, not deep in the camp.
      x = Math.max(i0 + 1, Math.min(i1 - 1, map.plaza.x));
      y = Math.max(j0 + 1, Math.min(j1 - 1, map.plaza.y));
    }
    return this.floor(map, x, y);
  }

  private floor(map: GameMap, x: number, y: number, r = 4): Point | null {
    if (x < 1 || y < 1 || x > map.w - 1 || y > map.h - 1) return null;
    if (freeAt(map, x, y)) return { x, y };
    const a = nearestFloor(map, x, y, r);
    return a ? { x: a[0], y: a[1] } : null;
  }

  private cry(ev: ActiveEvent): void {
    const v = ev.fighters.find((f) => !f.down) ?? ev.fighters[0]!;
    this.l.bark(v.e, ev.def.cry[Math.floor(this.l.rnd() * ev.def.cry.length)]!);
    ev.cryT = 9;
  }

  private run(ev: ActiveEvent, dt: number): void {
    const w = this.w;
    const p = w.player;
    ev.t += dt;
    ev.cryT -= dt;
    // Foes gone from the world (killed, by anyone) leave the fight.
    for (let k = ev.foes.length - 1; k >= 0; k--) {
      const f = ev.foes[k]!;
      if (!w.entities.has(f.e.id)) ev.foes.splice(k, 1);
      else if (f.e.dead) {
        // Fallen: gone once the fall has played (an event's foes never come back).
        this.leftovers.push({ e: f.e, t: 5 });
        ev.foes.splice(k, 1);
      }
    }
    if (ev.phase === 'over') {
      ev.overT -= dt;
      if (ev.overT <= 0) this.end(ev);
      return;
    }
    if (ev.phase === 'tell' && ev.t >= LIFE_EVENT.tell) ev.phase = 'fight';
    if (ev.cryT <= 0 && ev.fighters.some((f) => !f.down)) this.cry(ev);

    // The hurt disciple: the player helps them up by standing by; else the teacher comes for them.
    if (!ev.def.foe) {
      const v = ev.fighters[0]!;
      if (!p.dead && Math.hypot(p.x - v.e.x, p.y - v.e.y) < LIFE_EVENT.helpUpR) {
        ev.helpT += dt;
        if (ev.helpT >= LIFE_EVENT.helpUpT) {
          ev.helped = true;
          return this.finish(ev, 'helped');
        }
      } else ev.helpT = 0;
      if (ev.t > LIFE_EVENT.teacherAfter && ev.t - dt <= LIFE_EVENT.teacherAfter) {
        const teacher = this.l.people().find((x) => x.job === 'teacher' && x.e);
        if (teacher) this.l.sendTo(teacher.id, v.e.x + 0.8, v.e.y);
      }
      if (ev.t > LIFE_EVENT.teacherAfter + 15) this.finish(ev, 'lost');
      return;
    }

    const standing = ev.fighters.filter((f) => !f.down);
    for (const f of ev.fighters) {
      if (f.down) continue;
      f.e.combatT = 2;
      f.swingT -= dt;
      const foe = this.nearest(f.e, ev.foes.map((x) => x.e));
      if (!foe) continue;
      const d = Math.hypot(foe.x - f.e.x, foe.y - f.e.y);
      this.l.h.face(f.e, foe);
      if (d > LIFE_EVENT.reach && ev.phase === 'fight') {
        // Into reach (the one in trouble holds their ground; the escort and guards step up).
        const s = Math.min(d - LIFE_EVENT.reach * 0.8, f.e.speed * dt);
        f.e.moving = this.l.h.move(f.e, ((foe.x - f.e.x) / d) * s, ((foe.y - f.e.y) / d) * s);
      } else if (f.swingT <= 0 && ev.phase === 'fight') {
        f.swingT = LIFE_EVENT.fighterEvery * (0.85 + this.l.rnd() * 0.3);
        f.e.swingT = 0.32;
        this.l.h.emit({ type: 'basic', srcId: f.e.id, tgtId: foe.id, step: Math.floor(this.l.rnd() * 3) });
        this.l.h.hit(f.e, foe, 1, 'attack', 'basic');
      }
    }
    for (const fo of ev.foes) {
      const e = fo.e;
      // A foe the player has taken on is the World's to fight.
      if (e.targetId === p.id && e.brain?.state !== 'idle') continue;
      if (ev.phase !== 'fight') {
        this.l.h.face(e, ev.fighters[0]!.e);
        continue;
      }
      const target = this.nearest(e, standing.map((x) => x.e));
      const tf = standing.find((x) => x.e === target) ?? null;
      if (!target || !tf) continue;
      const d = Math.hypot(target.x - e.x, target.y - e.y);
      this.l.h.face(e, target);
      if (fo.windT > 0) {
        fo.windT -= dt;
        if (fo.windT <= 0 && fo.at && !fo.at.down && d < LIFE_EVENT.reach + 0.6) this.strike(ev, fo.at);
        continue;
      }
      if (d > LIFE_EVENT.reach) {
        const s = Math.min(d - LIFE_EVENT.reach * 0.8, e.speed * dt);
        e.moving = this.l.h.move(e, ((target.x - e.x) / d) * s, ((target.y - e.y) / d) * s);
        continue;
      }
      fo.swingT -= dt;
      if (fo.swingT <= 0) {
        // The wind-up the player can read: the swing starts, the blow lands after it.
        fo.swingT = this.range(LIFE_EVENT.foeEvery);
        fo.windT = LIFE_EVENT.windup;
        fo.at = tf;
        e.swingT = LIFE_EVENT.windup;
        e.combatT = 2;
        this.l.h.emit({ type: 'basic', srcId: e.id, tgtId: target.id, step: 0 });
      }
    }
    if (!ev.foes.length) return this.finish(ev, ev.helped ? 'helped' : 'won');
    if (ev.fighters.every((f) => f.down)) return this.finish(ev, 'lost');
    if (ev.t > LIFE_EVENT.most) this.finish(ev, ev.helped ? 'helped' : ev.fighters[0]!.down ? 'lost' : 'won');
  }

  private strike(ev: ActiveEvent, f: Fighter): void {
    const dmg = Math.round(this.range(LIFE_EVENT.foeDamage));
    f.hp = Math.max(0, f.hp - dmg);
    f.e.hp = Math.max(1, Math.round(f.hp));
    f.e.hitT = 0.25;
    if (f.hp <= 0) this.down(ev, f);
  }

  /** Down and out of the fight: crouched, hurt. */
  private down(ev: ActiveEvent, f: Fighter): void {
    f.down = true;
    f.e.combatT = 0;
    f.e.emote = 'life-hide';
    f.e.emoteT = 0;
    this.l.bark(f.e, ev.def.hurt[Math.floor(this.l.rnd() * ev.def.hurt.length)]!);
  }

  private nearest(from: Entity, list: readonly Entity[]): Entity | null {
    let best: Entity | null = null;
    let bd = Infinity;
    for (const e of list) {
      if (e.dead) continue;
      const d = Math.hypot(e.x - from.x, e.y - from.y);
      if (d < bd) [best, bd] = [e, d];
    }
    return best;
  }

  /** The outcome: thanks and a gift, a word of victory, or the hurt going home. */
  private finish(ev: ActiveEvent, outcome: 'won' | 'helped' | 'lost'): void {
    ev.phase = 'over';
    ev.outcome = outcome;
    ev.overT = 4;
    const v = ev.fighters[0]!;
    const say = (pool: readonly string[]) => this.l.bark(v.e, pool[Math.floor(this.l.rnd() * pool.length)]!);
    if (outcome === 'helped') {
      say(ev.def.thanks);
      this.reward(ev);
    } else if (outcome === 'won') say(ev.def.won);
    for (const f of ev.fighters) f.e.combatT = 0;
    // The foes still about slink off (those fighting the player stay with the player).
    for (const fo of ev.foes) this.leftovers.push({ e: fo.e, t: LIFE_EVENT.linger });
  }

  private reward(ev: ActiveEvent): void {
    const w = this.w;
    const pr = w.progress;
    const g = ev.def.gift;
    const v = ev.fighters[0]!;
    if (g.good && g.n) {
      const goods = pr.trades.goods;
      goods[g.good] = Math.min(999, (goods[g.good] ?? 0) + g.n);
    }
    if (g.gold) pr.gold += g.gold;
    const what = [g.good && g.n ? `${GOODS[g.good]?.name ?? g.good} ×${g.n}` : '', g.gold ? `${g.gold} ตำลึง` : ''].filter(Boolean);
    this.l.h.log('sys', `${v.e.name}มอบของขอบคุณ: ${what.join(' และ ')}`);
    const mem = memoryOf(pr);
    const m = ev.def.memory;
    const vk = v.id ? this.l.memoryKeyOf(v.id) : memoryKey(w, v.e);
    const emit = (x: WorldEvent) => this.l.h.emit(x);
    if (vk) {
      addScore(mem.npc, vk, m.victim);
      addAffinity(w, emit, vk, Math.round(m.victim * AFFINITY.helpedMult), v.e.name);
    }
    if (v.id)
      for (const t of this.l.tiesOf(v.id)) {
        const k = this.l.memoryKeyOf(t);
        if (k) {
          addScore(mem.npc, k, m.ties);
          addAffinity(w, emit, k, AFFINITY.helpedTies, t);
        }
      }
    if (m.town) addScore(mem.town, w.map.zone, m.town);
    this.l.rumour(v.e.name, ev.x, ev.y);
    this.l.h.emit({ type: 'progressChanged' });
  }

  private end(ev: ActiveEvent): void {
    const w = this.w;
    this.active = null;
    for (const f of ev.fighters) {
      f.e.combatT = 0;
      f.e.emote = null;
      if (f.id) {
        this.l.hold(f.id, false);
        if (f.down || (ev.outcome === 'lost' && f === ev.fighters[0])) this.l.injure(f.id);
      } else {
        // The actors go on their way (out of the world).
        w.entities.delete(f.e.id);
      }
    }
  }

  private release(fighters: Fighter[]): void {
    for (const f of fighters) {
      if (f.id) this.l.hold(f.id, false);
      else this.w.entities.delete(f.e.id);
    }
  }

  private tidy(dt: number): void {
    const w = this.w;
    const p = w.player;
    for (let k = this.leftovers.length - 1; k >= 0; k--) {
      const o = this.leftovers[k]!;
      if (!w.entities.has(o.e.id)) {
        this.leftovers.splice(k, 1);
        continue;
      }
      if (!o.e.dead && o.e.targetId === p.id && o.e.brain?.state !== 'idle') continue;
      if ((o.t -= dt) <= 0) {
        w.entities.delete(o.e.id);
        this.leftovers.splice(k, 1);
      }
    }
  }

  observe(ev: WorldEvent): void {
    const a = this.active;
    if (!a) return;
    const p = this.w.player;
    if (ev.type === 'hit' && ev.srcId === p.id && a.foes.some((f) => f.e.id === ev.tgtId)) a.helped = true;
  }
}
